import { AsyncState, Option, unloadedAsyncState } from "widgets-for-react";
import { initialJobState, Job, JobState } from "../../forms/job/jobState";
import { UserSubscriptions } from "../../subscription/subscriptionState";

export type SessionSetter = (user: User | undefined, session: SessionId | undefined, role: string | undefined) => void
export type SessionGetter = () => Option<UserSession>

export interface UserSession {
  readonly user: User
  readonly session: SessionId
  readonly role: string | undefined
}

export type Role = string

export interface BasicCredentials { email: string, password: string }
export interface LoginData extends BasicCredentials { show_password: boolean, role?: string, pin?: string }

export const emptyLoginData: LoginData = { email: '', password: "", show_password: false }

export interface ChooseRoleData extends BasicCredentials { role?: string, roles: string[], pin?: string }

// export interface LoginDataTwoFactorData extends BasicCredentials { pin:string }

export interface ChangePasswordData {
  old_password: string | undefined,
  new_password: string | undefined,
  new_password_confirmed: string | undefined,
  show_password: boolean
}

export interface SignUpData { 
  password:string|undefined
  showPassword:boolean
  confirmed_password:string|undefined,
  email:string|undefined,
  confirmed_email:string|undefined,
  name:string|undefined,
  surname:string|undefined,
  phone: string|undefined,
  useTwoFactor: boolean,
  marketingEmails: boolean,
  termsAndConditions: boolean,
  promoCode: string,
  selectedJob: JobState
  jobs: AsyncState<Job[]>
  isStudent: boolean
}

export interface ResetPasswordSuccessData extends BasicCredentials { }
export interface SignupSuccessData extends BasicCredentials { }

export interface ResetPasswordData {
  password: string | undefined,
  email: string,
  confirmed_password: string | undefined,
  reset_password_token: string | undefined,
  show_password: boolean
}

export interface VerifyResetPasswordTokenData { reset_password_token: string | undefined, email: string }

export interface ActivateAccountData { token: string | null, email: string | null, newPassword: string | undefined, newPasswordConfirmed: string | undefined, showPassword: boolean, redirect: string }

export interface ResetPasswordRequestData { email: string }

export interface ResendActivationData { email:string|null }
export interface ValidateSessionData {
  email: string
  userKind : string
}

export interface PasswordKind { kind: "standard" | "two factor" }

export type Token = string



export type User =
{ 
  id: string
  username: string
  email: string
} 

export type SessionId = string

export type PrivacyKind = "standard" | "strict"

export interface LoggedInState extends IAuthState {
  readonly loggedInState: "logged in"
  | "logged in - activation success"
  | "logged in - signup success"
  | "logged in - logout api error"
  | "logged in - logging out",
  readonly user: User,
  readonly role: Role,
  readonly session: SessionId,
  readonly userSubscriptions: UserSubscriptions
}

export function isLoggedInState(s: AuthState): s is LoggedInState {
  const asLoggedIn = (s as LoggedInState)
  return asLoggedIn.loggedInState == "logged in" ||
    asLoggedIn.loggedInState == "logged in - activation success" ||
    asLoggedIn.loggedInState == "logged in - signup success" ||
    asLoggedIn.loggedInState == "logged in - logout api error" ||
    asLoggedIn.loggedInState == "logged in - logging out"
}

export interface LoggedOutState extends IAuthState {
  readonly loggedOutState: "logged out"
  | "logged out - logging in"
  | "logged out - must reset password"
  | "logged out - login api error"
  | "logged out - error login not authorized"
  | "logged out - error account not active"
  | "logged out - error too many reset password attempts"
  | "logged out - error reset password token has expired"
  | "logged out - error reset password token not found"
  | "logged out - error too many account activation attempts"
  | "logged out - error account activation token not found"
  | "logged out - error too many attempts"
  | "logged out - error account blocked"
  | "logged in - redirecting"
  | "two factor"
  | "two factor - logging"
  | "two factor - error wrong code"
  | "two factor - error empty code"
  | "two factor - login api error"
  | "two factor - resend api error"
  | "two factor - resending pin"
  | "two factor - error too many attempts",
  readonly login_data: LoginData
}

export function isLoggedOutState(s: AuthState): s is LoggedOutState {
  let asLoggedOut = s as LoggedOutState
  return asLoggedOut.loggedOutState == "logged out" ||
    asLoggedOut.loggedOutState == "logged out - logging in" ||
    asLoggedOut.loggedOutState == "logged out - login api error" ||
    asLoggedOut.loggedOutState == "logged out - error login not authorized" ||
    asLoggedOut.loggedOutState == "logged out - error too many attempts" ||
    asLoggedOut.loggedOutState == "logged out - error account blocked" ||
    asLoggedOut.loggedOutState == "logged out - error too many reset password attempts" ||
    asLoggedOut.loggedOutState == "logged out - error reset password token has expired" ||
    asLoggedOut.loggedOutState == "logged out - error reset password token not found" ||
    asLoggedOut.loggedOutState == "logged out - error too many account activation attempts" ||
    asLoggedOut.loggedOutState == "logged out - error account activation token not found" ||
    asLoggedOut.loggedOutState == "logged out - error account not active" ||
    asLoggedOut.loggedOutState == "two factor" ||
    asLoggedOut.loggedOutState == "two factor - logging" ||
    asLoggedOut.loggedOutState == "two factor - error empty code" ||
    asLoggedOut.loggedOutState == "two factor - error wrong code" ||
    asLoggedOut.loggedOutState == "two factor - resending pin" ||
    asLoggedOut.loggedOutState == "two factor - resend api error" ||
    asLoggedOut.loggedOutState == "two factor - login api error" ||
    asLoggedOut.loggedOutState == "logged in - redirecting" ||
    asLoggedOut.loggedOutState == "two factor - error too many attempts" ||
    asLoggedOut.loggedOutState == "logged out - must reset password"
}

export interface SignUpState extends IAuthState {
  readonly signUpState: "sign up"
  | "sign up - signing up"
  | "sign up - success"
  | "sign up - promo code"
  | "sign up - error invalid promo code"
  | "sign up - api error"
  | "sign up - error form validation"
  | "sign up - error invalid email"
  | "sign up - error weak password"
  | "sign up - error invalid username"
  | "sign up - error account not active"
  | "sign up - error account already exists"
  | "sign up - error too many attempts"
  readonly login_data: SignUpData
}

export function isSignUpState(s: AuthState): s is SignUpState {
  const asSignup = s as SignUpState
  return asSignup.signUpState == "sign up" ||
    asSignup.signUpState == "sign up - signing up" ||
    asSignup.signUpState == "sign up - success" ||
    asSignup.signUpState == "sign up - promo code" ||
    asSignup.signUpState == "sign up - error invalid promo code" ||
    asSignup.signUpState == "sign up - api error" ||
    asSignup.signUpState == "sign up - error form validation" ||
    asSignup.signUpState == "sign up - error invalid email" ||
    asSignup.signUpState == "sign up - error weak password" ||
    asSignup.signUpState == "sign up - error invalid username" ||
    asSignup.signUpState == "sign up - error account not active" ||
    asSignup.signUpState == "sign up - error account already exists" ||
    asSignup.signUpState == "sign up - error too many attempts"
}

export interface ResendActivationState extends IAuthState {
  readonly resendActivationState: "resend activation"
  | "resend activation - success"
  | "resend activation - api error"
  | "resend activation - resending"
  | "resend activation - error token expired"
  | "resend activation - error too many attempts"
  readonly login_data: ResendActivationData
}

export function isResendActivationState(s: AuthState): s is ResendActivationState {
  const asResendActivation = s as ResendActivationState
  return asResendActivation.resendActivationState == "resend activation" ||
    asResendActivation.resendActivationState == "resend activation - success" ||
    asResendActivation.resendActivationState == "resend activation - api error" ||
    asResendActivation.resendActivationState == "resend activation - resending" ||
    asResendActivation.resendActivationState == "resend activation - error token expired" ||
    asResendActivation.resendActivationState == "resend activation - error too many attempts"
}

export interface ResetPasswordState extends IAuthState {
  readonly resetPasswordState: "reset password"
  | "reset password - resetting password"
  | "reset password - api error"
  | "reset password - error weak password"
  | "reset password - error too many attempts"
  | "reset password - error token not valid"
  | "reset password success"
  readonly login_data: ResetPasswordData
}

export function isResetPasswordState(s: AuthState): s is ResetPasswordState {
  const asResetPassword = s as ResetPasswordState
  return asResetPassword.resetPasswordState == "reset password" ||
    asResetPassword.resetPasswordState == "reset password - resetting password" ||
    asResetPassword.resetPasswordState == "reset password - api error" ||
    asResetPassword.resetPasswordState == "reset password - error weak password" ||
    asResetPassword.resetPasswordState == "reset password - error too many attempts" ||
    asResetPassword.resetPasswordState == "reset password - error token not valid" ||
    asResetPassword.resetPasswordState == "reset password success"
}
export interface VerifyResetPasswordTokenState extends IAuthState {
  readonly verifyResetPasswordTokenState: "verify reset password token"
  | "verify reset password token - sending"
  | "verify reset password token - api error"
  readonly login_data: VerifyResetPasswordTokenData
}

export function isVerifyResetPasswordTokenState(s: AuthState): s is VerifyResetPasswordTokenState {
  const asVerifyResetToken = s as VerifyResetPasswordTokenState
  return asVerifyResetToken.verifyResetPasswordTokenState == "verify reset password token" ||
    asVerifyResetToken.verifyResetPasswordTokenState == "verify reset password token - sending" ||
    asVerifyResetToken.verifyResetPasswordTokenState == "verify reset password token - api error"
}

export interface ActivateAccountState extends IAuthState {
  readonly activateAccountState: "activate account"
  | "activate account - activating"
  | "activate account - api error"
  | "activate account - error token not found"
  | "activate account - error account activation failed"
  | "activate account - error token expired"
  | "activate account - account activated"
  | "activate account - change password"
  | "activate account - changing password"
  | "activate account - error weak password"
  readonly activateAccountData: ActivateAccountData
}

export function isActivateAccountState(s: AuthState): s is ActivateAccountState {
  const asActivateAccount = s as ActivateAccountState
  return asActivateAccount.activateAccountState == "activate account" ||
    asActivateAccount.activateAccountState == "activate account - activating" ||
    asActivateAccount.activateAccountState == "activate account - api error" ||
    asActivateAccount.activateAccountState == "activate account - error token not found" ||
    asActivateAccount.activateAccountState == "activate account - error account activation failed" ||
    asActivateAccount.activateAccountState == "activate account - error token expired" ||
    asActivateAccount.activateAccountState == "activate account - account activated" ||
    asActivateAccount.activateAccountState == "activate account - change password" ||
    asActivateAccount.activateAccountState == "activate account - changing password" ||
    asActivateAccount.activateAccountState == "activate account - error weak password"
}

export interface ResetPasswordRequestState extends IAuthState {
  readonly resetPasswordRequestState: "reset password request"
  | "reset password request - success"
  | "reset password request - resetting"
  | "reset password request - error form validation"
  | "reset password request - api error"
  | "reset password request - error too many attempts"
  | "reset password request - error user not activated"
  | "reset password request - error email not valid"
  | "reset password request - error email not found"
  readonly login_data: ResetPasswordRequestData
}

export function isResetPasswordRequestState(s: AuthState): s is ResetPasswordRequestState {
  const asResetPasswordRequest = s as ResetPasswordRequestState
  return asResetPasswordRequest.resetPasswordRequestState == "reset password request" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - success" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - resetting" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - error form validation" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - api error" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - error too many attempts" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - error user not activated" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - error email not valid" ||
    asResetPasswordRequest.resetPasswordRequestState == "reset password request - error email not found"
}

export interface IAuthState {
  readonly status: 'logged in' | 'logged out' | 'verify authentication'
}

export const defaultAuthState: AuthState = {
  status: 'verify authentication',
  activateAccountState: 'activate account',
  loggedInState: 'logged in',
  loggedOutState: 'logged out',
  resendActivationState: 'resend activation',
  resetPasswordRequestState: 'reset password request',
  resetPasswordState: 'reset password',
  signUpState: 'sign up',
  login_data: {
    email: '',
    password: undefined,
    showPassword: false,
    confirmed_email: '',
    confirmed_password: '',
    name: '',
    reset_password_token: '',
    pin: '',
    role: '',
    jobs: unloadedAsyncState(),
    selectedJob: initialJobState(),
    termsAndConditions: false,
    marketingEmails: false,
  },
  role: '',
  session: '',
  activateAccountData: {
    email: '',
    token: '',
    newPassword: '',
    newPasswordConfirmed: '',
    showPassword: false,
    redirect: '',
  },
}

export type AuthState = (LoggedInState
  | LoggedOutState
  | SignUpState
  | ResetPasswordState
  | ResetPasswordRequestState
  | ActivateAccountState
  | ResendActivationState
  | VerifyResetPasswordTokenState)
  & IAuthState

